import React, { createContext, useState, useContext, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('access_token');
      const savedUser = localStorage.getItem('baristaUser');
      const savedDeviceToken = localStorage.getItem('device_token');
      
      // Если есть все токены - восстанавливаем сессию
      if (savedToken && savedUser && savedDeviceToken) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          console.log('[Auth] Session restored from localStorage');
          console.log('[Auth] Device token present:', savedDeviceToken.substring(0, 10) + '...');
        } catch (error) {
          console.error('[Auth] Error parsing saved user:', error);
          console.error('[Auth] Corrupted baristaUser data:', savedUser);
          console.error('[Auth] Clearing corrupted data but keeping device_token for re-login');
          // Удаляем только поврежденные данные
          localStorage.removeItem('baristaUser');
          localStorage.removeItem('access_token');
          // НЕ удаляем device_token и device_id - они могут быть валидными
          // Пользователь останется на странице входа, но device_token сохранен
        }
      } 
      // Если есть device_token, но нет JWT - обмениваем автоматически
      else if (savedDeviceToken && !savedToken) {
        console.log('[Auth] Found device_token but no JWT - exchanging for new JWT...');
        try {
          // Обмениваем device_token на JWT
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/device`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              device_token: savedDeviceToken,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            const { access_token } = data;

            if (access_token) {
              // Сохраняем JWT
              localStorage.setItem('access_token', access_token);
              console.log('[Auth] JWT token obtained from device_token');

              // Декодируем JWT для получения информации
              try {
                const payload = JSON.parse(atob(access_token.split('.')[1]));
                const userData = {
                  deviceId: payload.sub,
                  coffeeShopId: payload.coffee_shop_id,
                  tokenType: payload.token_type || 'device',
                };
                setUser(userData);
                localStorage.setItem('baristaUser', JSON.stringify(userData));
                console.log('[Auth] Session restored from device_token');
              } catch (parseError) {
                console.error('[Auth] Error parsing JWT:', parseError);
              }
            }
          } else {
            console.error('[Auth] Failed to exchange device_token for JWT:', response.status);
            // Очищаем только при ошибках авторизации (401, 403)
            // При других ошибках (500, 502, сеть) - сохраняем device_token
            if (response.status === 401 || response.status === 403) {
              console.error('='.repeat(60));
              console.error('[Auth] ⚠️ DELETING device_token - Invalid or expired (401/403)');
              console.error('[Auth] Device token:', savedDeviceToken.substring(0, 20) + '...');
              console.error('[Auth] Response status:', response.status);
              console.error('='.repeat(60));
              localStorage.removeItem('device_token');
              localStorage.removeItem('device_id');
              localStorage.removeItem('baristaUser');
            } else {
              console.warn('[Auth] Server error or network issue - keeping device_token for retry');
              console.warn('[Auth] Response status:', response.status);
            }
          }
        } catch (error) {
          console.error('[Auth] Network error exchanging device_token:', error);
          console.warn('[Auth] Keeping device_token for retry');
          // При сетевых ошибках НЕ удаляем device_token
        }
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (deviceToken) => {
    try {
      // Validate device token format (should be <device_id>.<secret>)
      if (!deviceToken || !deviceToken.includes('.')) {
        throw new Error('Неверный формат токена устройства. Ожидается формат: <device_id>.<secret>');
      }

      const deviceId = deviceToken.split('.')[0];

      // Exchange long-lived device token for short-lived JWT token
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/device`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_token: deviceToken, // Long-lived device token (format: <device_id>.<secret>)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Неверный токен устройства');
      }

      const data = await response.json();
      const { access_token } = data; // Short-lived JWT token

      // Validate token format before storing
      if (!access_token || typeof access_token !== 'string') {
        throw new Error('Неверный формат токена в ответе сервера');
      }

      // Validate JWT format (should have 3 parts separated by dots)
      const tokenParts = access_token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Неверный формат JWT токена');
      }

      // ВАЖНО: Сохраняем долгоиграющий device_token для обновления JWT
      // Этот токен используется для повторного обмена на новый JWT при истечении
      localStorage.setItem('device_token', deviceToken);
      localStorage.setItem('device_id', deviceId);
      console.log('[Auth] Long-lived device token stored for future JWT refresh');

      // Сохраняем короткоживущий JWT токен
      // Этот токен используется для всех последующих API запросов и SSE соединений
      localStorage.setItem('access_token', access_token);
      console.log('[Auth] Short-lived JWT token stored successfully');

      // Декодируем JWT токен для получения информации об устройстве
      try {
        const payload = JSON.parse(atob(access_token.split('.')[1]));
        const userData = {
          deviceId: payload.sub,
          coffeeShopId: payload.coffee_shop_id,
          tokenType: payload.token_type,
        };
        setUser(userData);
        localStorage.setItem('baristaUser', JSON.stringify(userData));
        return userData;
      } catch (parseError) {
        console.error('Error parsing JWT token:', parseError);
        // Если не удалось распарсить токен, все равно сохраняем его
        const userData = {
          deviceId: deviceId,
          coffeeShopId: null,
          tokenType: 'device',
        };
        setUser(userData);
        localStorage.setItem('baristaUser', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('='.repeat(60));
    console.log('[Auth] ℹ️ User logout - clearing all tokens');
    console.log('='.repeat(60));
    setUser(null);
    localStorage.removeItem('baristaUser');
    localStorage.removeItem('access_token');
    localStorage.removeItem('device_token');
    localStorage.removeItem('device_id');
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};

